import React, { useState, useEffect, useRef } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import "./WhatsappSettings.css";

const WhatsappSettings = ({ userId }) => {
  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsappNumber: "",
    whatsappMessage: ""
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [availableSnippets] = useState([
    "lead_name", "lead_email", "lead_telephone", "lead_address", 
    "lead_city", "lead_rating", "lead_reviews", "lead_stars", 
    "business_name", "lead_instagram", "lead_website", "lead_type"
  ]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  // Load existing WhatsApp settings from database
  useEffect(() => {
    if (!userId) return;
    
    const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
    const unsubscribe = onValue(
      whatsappSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setWhatsappSettings(snapshot.val());
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading WhatsApp settings", error);
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWhatsappSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setWhatsappSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCursorPosition(e.target.selectionStart);
  };

  const insertSnippet = (snippet) => {
    const textareaElement = textareaRef.current;
    if (!textareaElement) return;
    
    const start = textareaElement.selectionStart;
    const end = textareaElement.selectionEnd;
    const text = whatsappSettings.whatsappMessage;
    
    // Add space before snippet if not at beginning and previous character is not a space
    const needsSpace = start > 0 && text[start - 1] !== ' ';
    const snippetText = needsSpace ? ` {{${snippet}}}` : `{{${snippet}}}`;
    const newText = text.substring(0, start) + snippetText + text.substring(end);
    
    setWhatsappSettings(prev => ({
      ...prev,
      whatsappMessage: newText
    }));
    
    // After updating text, position cursor after inserted snippet
    setTimeout(() => {
      textareaElement.focus();
      const newPosition = start + snippetText.length;
      textareaElement.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("No user logged in.");
      return;
    }
    
    try {
      // Format phone number (remove spaces and other non-numeric characters)
      const formattedNumber = whatsappSettings.whatsappNumber.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
      
      const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
      await update(whatsappSettingsRef, {
        ...whatsappSettings,
        whatsappNumber: formattedNumber
      });
      
      setMessage("WhatsApp settings saved successfully.");
    } catch (error) {
      console.error("Error saving WhatsApp settings:", error);
      setMessage("Error saving settings: " + error.message);
    }
  };

  // Generate preview of WhatsApp message with highlighted snippets
  const generatePreview = () => {
    if (!whatsappSettings.whatsappMessage) return "";
    
    // Replace all snippets in format {{snippet}} with highlighted representation
    return whatsappSettings.whatsappMessage.replace(
      /\{\{([a-zA-Z_]+)\}\}/g, 
      '<span class="highlighted_snippet">{{$1}}</span>'
    );
  };

  if (loading) {
    return <p className="whatsapp_loading_message">Loading WhatsApp settings...</p>;
  }

  return (
    <div className="whatsapp_container">
      <div className="whatsapp_settings_section">
        <h3 className="whatsapp_title">WhatsApp Settings</h3>
        
        <div className="whatsapp_setting_group">
          <div className="whatsapp_setting_header">
            <label className="whatsapp_setting_label">
              WhatsApp Phone Number
            </label>
            <span className="whatsapp_setting_description">
              Enter the WhatsApp number with country code (e.g. +491234567890).
            </span>
          </div>
          
          <div className="whatsapp_setting_body">
            <input
              type="text"
              name="whatsappNumber"
              value={whatsappSettings.whatsappNumber}
              onChange={handleChange}
              placeholder="e.g. +49123456789"
              className="whatsapp_setting_input"
            />
          </div>
        </div>
        
        <div className="whatsapp_setting_group">
          <div className="whatsapp_setting_header">
            <label className="whatsapp_setting_label">
              WhatsApp Message
            </label>
            <span className="whatsapp_setting_description">
              Compose your WhatsApp message. Click on any Smart Snippet below to insert it at the current cursor position.
            </span>
          </div>
          
          <div className="whatsapp_setting_body">
            <div className="whatsapp_message_editor">
              <div className="whatsapp_snippets_help">
                <div className="whatsapp_snippets_help_title">Click to insert Smart Snippets:</div>
                <div className="whatsapp_snippets_help_list">
                  {availableSnippets.map((snippet) => (
                    <button
                      key={snippet}
                      className="whatsapp_snippet_help_item"
                      onClick={() => insertSnippet(snippet)}
                      type="button"
                    >
                      {"{{"}{snippet}{"}}"}
                    </button>
                  ))}
                </div>
              </div>
              
              <textarea
                ref={textareaRef}
                name="whatsappMessage"
                value={whatsappSettings.whatsappMessage}
                onChange={handleTextareaChange}
                placeholder="Enter your message here. Use Smart Snippets like {{lead_name}} to insert dynamic values."
                rows="8"
                className="whatsapp_setting_textarea"
              />
            </div>
          </div>
        </div>
        
        <div className="whatsapp_preview">
          <h4>Preview:</h4>
          <div className="whatsapp_preview_container">
            <div className="whatsapp_preview_header">
              <div className="whatsapp_preview_contact">
                <span className="whatsapp_preview_name">
                  {whatsappSettings.whatsappNumber || "+49 123 456789"}
                </span>
              </div>
            </div>
            <div 
              className="whatsapp_preview_message"
              dangerouslySetInnerHTML={{ __html: generatePreview() }}
            />
          </div>
        </div>
      </div>

      <div className="whatsapp_setting_actions">
        <button className="whatsapp_save_button" onClick={handleSave}>
          Save WhatsApp Settings
        </button>
      </div>
      
      {message && (
        <p className={`whatsapp_message ${message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default WhatsappSettings; 